import React, { useRef, useEffect, useState } from "react";

export default function WaveformVisualizer({
  analyser: external,
  opacity = 0.95,
  blend = "screen",
  zIndex = 50,
  blur = 20,
  brightness = 1,
}) {
  const canvasRef = useRef(null);
  const [internal, setInternal] = useState(null);

  useEffect(() => {
    if (external) return;
    let ctx, stream, analyser;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((s) => {
        stream = s;
        ctx = new AudioContext();
        const src = ctx.createMediaStreamSource(stream);
        analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0;
        src.connect(analyser);
        setInternal(analyser);
      })
      .catch((err) => console.warn("Mic permission denied → 波形静默", err));

    return () => {
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close();
    };
  }, [external]);

  useEffect(() => {
    const analyser = external || internal;
    if (!analyser) return;

    const canvas = canvasRef.current;
    const ctx2d = canvas.getContext("2d");
    const buffer = new Uint8Array(analyser.frequencyBinCount);

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      requestAnimationFrame(draw);
      analyser.getByteFrequencyData(buffer);
      ctx2d.clearRect(0, 0, canvas.width, canvas.height);

      const barW = canvas.width / buffer.length;

      for (let i = 0; i < buffer.length / 2; i++) {
        const v = buffer[i];
        const norm = Math.pow(v / 255, 0.35); // γ=0.35 更有弹性
        const boostLow = i < buffer.length * 0.2 ? 1.8 : 1.0; // 低频增强
        const barH = norm * canvas.height * 0.12 * boostLow;

        const progress = i / (buffer.length / 2);
        const sag = Math.cos(progress * Math.PI) * 40; // 中间轻微下垂
        const hue = (i / buffer.length) * 360;
        const alpha = 0.4 + norm * 0.6;
        const color = `hsla(${hue}, 100%, 70%, ${alpha})`;

        const xLeft = i * barW;
        const xRight = (buffer.length - 1 - i) * barW;

        ctx2d.fillStyle = color;
        ctx2d.fillRect(xLeft, canvas.height - barH + sag, barW * 0.9, barH);
        ctx2d.fillRect(xRight, canvas.height - barH + sag, barW * 0.9, barH);
      }
    };
    draw();

    return () => window.removeEventListener("resize", resize);
  }, [external, internal]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex,
        pointerEvents: "none",
        mixBlendMode: blend,
        opacity,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          filter: `blur(${blur}px) brightness(${brightness})`,
        }}
      />
    </div>
  );
}
